import { apiRequest, getAuthHeaders, API_BASE } from '../../shared/api/base'
import { supabase } from '../../shared/lib/supabase'
import { convertToJpeg } from '../../shared/utils/image'
import type { ChoiceOption, TriageEmailData } from '../../shared/types/chat'
import type { BriefingData } from '../../shared/types/briefing'

// ─── Error types ────────────────────────────────────────────

export interface ApiError extends Error {
  status?: number
  code?: string
}

// ─── Response types ─────────────────────────────────────────

export interface ApprovalSummary {
  approvalId: string
  taskId: string
  actionType: string
  summary: string
  riskLevel: 'low' | 'medium' | 'high'
  status: 'pending' | 'approved' | 'denied' | 'expired'
  expiresAt: string
  extendedInfo?: Record<string, unknown>
}

export interface SentEmailSummary {
  to: string
  toEmail: string
  subject: string
  preview: string
  status: 'sent' | 'delivered'
}

/** Full assistant message shape — used by buildMessagesFromResponse and chat.complete handler. */
export interface PluginCardMapping {
  renderAs: string
  data: unknown
}

export interface AssistantMessageData {
  messageId: string
  conversationId: string
  role: 'assistant'
  content: string
  timestamp: string
  approvals?: ApprovalSummary[]
  choiceOptions?: ChoiceOption[]
  multiSelect?: boolean
  sentEmail?: SentEmailSummary
  pluginCards?: PluginCardMapping[]
}

export interface ConversationSummary {
  id: string
  updatedAt: string
  lastMessage: string
  isActive: boolean
  title: string | null
}

export interface ConversationsApiResponse {
  conversations: ConversationSummary[]
}

export interface ApiMessageApproval {
  id: string
  status: 'pending' | 'approved' | 'denied' | 'expired'
  riskLevel: 'low' | 'medium' | 'high'
  summary: string
  actionType: string
  extendedInfo?: Record<string, unknown>
}

/** Wire shape of a briefing card on the chat-message API.
 *  Same shape as the in-app `BriefingData` — the API preserves typed
 *  actions and `itemKey` so action pills can dispatch directly without
 *  needing a separate fetch. */
export type ApiBriefingCard = BriefingData

export interface ApiMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  approval?: ApiMessageApproval
  sentEmail?: SentEmailSummary
  briefingCard?: ApiBriefingCard
  triageEmail?: TriageEmailData
  pluginCards?: PluginCardMapping[]
  imageIds?: string[]
  sourceEmailId?: string
}

export { getEmailById, type EmailDetailResponse } from '../../shared/api/emails'

export interface MessagesApiResponse {
  messages: ApiMessage[]
  nextCursor: string | null
}

export interface ApproveResponse {
  approvalId: string
  taskId: string
  decision: string
  outcome: 'approved' | 'denied'
  decidedAt: string
  execution?: { success: boolean; result: string }
  reply?: { messageId: string; content: string; timestamp: string }
}

// ─── API functions ──────────────────────────────────────────

// ─── SSE streaming ──────────────────────────────────────────

export interface SseEvent {
  event: string
  data: Record<string, unknown>
}

/**
 * Send a message and stream the response via SSE.
 * Yields parsed SSE events: meta, delta, tool_activity, complete, error.
 */
export async function* sendMessageStream(
  content: string,
  conversationId?: string,
  hint?: string,
  imageIds?: string[],
): AsyncGenerator<SseEvent> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/chat/message`, {
    method: 'POST',
    headers: { ...headers, 'Accept': 'text/event-stream' },
    body: JSON.stringify({
      content,
      conversationId,
      ...(hint && { hint }),
      ...(imageIds?.length && { imageIds }),
    }),
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    const err = new Error(body?.error?.message ?? `API error: ${response.status}`)
    ;(err as ApiError).status = response.status
    ;(err as ApiError).code = body?.error?.code
    throw err
  }

  const reader = response.body?.getReader()
  if (!reader) throw new Error('No response body')

  const decoder = new TextDecoder()
  let buffer = ''
  let currentEvent = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      // Keep the last (potentially incomplete) line in the buffer
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim()
        } else if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6)
          try {
            const data = JSON.parse(jsonStr) as Record<string, unknown>
            yield { event: currentEvent, data }
          } catch {
            // Skip malformed JSON lines
          }
          currentEvent = ''
        }
        // Empty lines are SSE event delimiters — no action needed since we
        // process event+data pairs as soon as we see the data line
      }
    }
  } finally {
    reader.releaseLock()
  }
}

export async function createNewConversation(): Promise<{ conversationId: string }> {
  return apiRequest<{ conversationId: string }>('/chat/new', { method: 'POST' })
}

export async function getConversations(): Promise<ConversationsApiResponse> {
  return apiRequest<ConversationsApiResponse>('/chat/conversations')
}

export async function getMessages(
  conversationId: string,
  cursor?: string,
  limit: number = 50,
): Promise<MessagesApiResponse> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (cursor) params.set('cursor', cursor)
  return apiRequest<MessagesApiResponse>(
    `/chat/conversations/${conversationId}/messages?${params}`,
  )
}

export async function approveAction(
  approvalId: string,
  decision: 'allow_once' | 'allow_always' = 'allow_once',
  connectionId?: string,
  editedBody?: string,
): Promise<ApproveResponse> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/approvals/${approvalId}/approve`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      decision,
      ...(connectionId && { connectionId }),
      ...(editedBody !== undefined && { editedBody }),
    }),
  })

  // 200 = all good, 502 = approved but execution failed (response body is still valid)
  if (!res.ok && res.status !== 502) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.error?.message ?? `API error: ${res.status}`)
  }

  return res.json() as Promise<ApproveResponse>
}

export interface BatchApproveResult {
  approvalId: string
  outcome: 'approved' | 'denied' | 'error'
  taskId?: string
  execution?: { success: boolean; result: string }
  error?: string
}

export interface BatchApproveResponse {
  results: BatchApproveResult[]
  reply?: { messageId: string; content: string; timestamp: string }
}

export async function batchApproveActions(
  approvalIds: string[],
  decision: 'allow_once' | 'allow_always' = 'allow_once',
): Promise<BatchApproveResponse> {
  return apiRequest<BatchApproveResponse>('/approvals/batch', {
    method: 'POST',
    body: JSON.stringify({ approvalIds, decision }),
  })
}

export async function analyzeTriageEmail(signalId: string): Promise<{ ok: boolean }> {
  return apiRequest(`/inbox/emails/${encodeURIComponent(signalId)}/analyze`, { method: 'POST' })
}

export async function blockTriageSender(signalId: string): Promise<{ ok: boolean }> {
  return apiRequest(`/inbox/emails/${encodeURIComponent(signalId)}/block-sender`, { method: 'POST' })
}

export async function claimTriageEmail(signalId: string): Promise<{ ok: boolean }> {
  return apiRequest(`/inbox/emails/${encodeURIComponent(signalId)}/claim`, { method: 'POST' })
}

/** Get signed URLs for an image by ID */
export async function getImageUrl(imageId: string): Promise<{ url: string; thumbnailUrl: string | null }> {
  return apiRequest<{ url: string; thumbnailUrl: string | null }>(`/images/${imageId}/url`)
}

/** Upload an image for chat (no notebook attachment) */
export async function uploadChatImage(file: File | Blob): Promise<{ id: string; url: string; thumbnailUrl: string | null }> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  // Convert to JPEG client-side (handles HEIC from iOS without server libheif)
  const jpegBlob = await convertToJpeg(file)

  const formData = new FormData()
  formData.append('file', jpegBlob, 'photo.jpg')

  const res = await fetch(`${API_BASE}/images/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
    body: formData,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.error?.message ?? `Upload failed: ${res.status}`)
  }

  const data = await res.json() as { id: string; url: string; thumbnailUrl: string | null }
  return { id: data.id, url: data.url, thumbnailUrl: data.thumbnailUrl }
}

/** Transcribe a voice clip via Whisper + Haiku cleanup */
export async function transcribeVoiceClip(audioBlob: Blob): Promise<{ transcript: string; message: string }> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const formData = new FormData()
  formData.append('file', audioBlob, 'voice.webm')

  const res = await fetch(`${API_BASE}/chat/voice`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
    body: formData,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.error?.message ?? `Transcription failed: ${res.status}`)
  }

  return res.json() as Promise<{ transcript: string; message: string }>
}

export async function denyAction(
  approvalId: string,
): Promise<ApproveResponse> {
  return apiRequest<ApproveResponse>(`/approvals/${approvalId}/deny`, {
    method: 'POST',
  })
}

/** Dismiss a briefing item directly (no LLM round-trip). Persists a Cortex
 *  entry with category='dismissed' and a 14-day expiry, so the item is
 *  suppressed from future briefings. */
export async function dismissBriefingItem(
  description: string,
  itemKey?: string,
): Promise<{ dismissed: string; expiresInDays: number }> {
  return apiRequest('/briefing/dismiss', {
    method: 'POST',
    body: JSON.stringify({ description, ...(itemKey && { itemKey }) }),
  })
}

/** Resolve a follow-up by ID. Used by briefing action pills with
 *  kind='resolve_followup'. */
export async function resolveFollowup(followupId: string): Promise<void> {
  await apiRequest(`/followups/${followupId}/resolve`, {
    method: 'PATCH',
  })
}
